import { useState, useMemo, useEffect } from "react";
import { Box, Stack, InputAdornment, CircularProgress } from "@mui/material";
import { Seo } from "src/components/seo";
import {
  CustomTypography,
  CustomTextField,
  CustomButton,
} from "src/components/inputs";
import { withToleranceGuard } from "src/hocs/with-role-based-guard";
import toast from "react-hot-toast";
import { useAuth } from "src/hooks/use-auth";
import { thunks } from "src/thunks/inbound-process";
import { useDispatch, useSelector } from "src/store";

const TolerancePage = () => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [below50, setBelow50] = useState("");
  const [between50And100, setBetween50And100] = useState("");
  const [above100, setAbove100] = useState("");
  const [saving, setSaving] = useState(false);
  const { brandTolerances, brandTolerancesLoading } = useSelector(
    (state) => state.inboundProcess
  );

  useEffect(() => {
    dispatch(thunks.getBrandTolerances());
  }, []);

  useEffect(() => {
    if (brandTolerances) {
      setBelow50(
        isNaturalNumber(brandTolerances?.ToleranceBelow50)
          ? brandTolerances?.ToleranceBelow50
          : ""
      );
      setBetween50And100(
        isNaturalNumber(brandTolerances?.Tolerance50To100)
          ? brandTolerances?.Tolerance50To100
          : ""
      );
      setAbove100(
        isNaturalNumber(brandTolerances?.ToleranceAbove100)
          ? brandTolerances?.ToleranceAbove100
          : ""
      );
    }
  }, [brandTolerances]);

  const isNaturalNumber = (val) => {
    if (val === "" || val === null || val === undefined) return false;
    const num = Number(val);
    return !Number.isNaN(num) && num >= 0;
  };

  const allValid = useMemo(() => {
    return (
      isNaturalNumber(below50) &&
      isNaturalNumber(between50And100) &&
      isNaturalNumber(above100)
    );
  }, [below50, between50And100, above100]);

  const handleSave = async () => {
    if (!allValid) return;
    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("ToleranceBelow50", below50);
      formData.append("Tolerance50To100", between50And100);
      formData.append("ToleranceAbove100", above100);

      await dispatch(thunks.updateBrandTolerances(formData));
    } catch (err) {
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Seo title="Dashboard: Tolerance" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 3,
          padding: "10px 34px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          style={{
            padding: 25,
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
            gap: 20,
          }}
        >
          <Stack
            style={{ width: "100%", maxWidth: 480, gap: 20, marginTop: 75 }}
          >
            <CustomTextField
              label="Tolerance level from 1 to 49 units per SKU"
              style={{ width: "100%" }}
              value={below50}
              handleChange={(e) => setBelow50(e.target.value)}
              inputProps={{
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  type: "number",
                },
              }}
            />
            <CustomTextField
              label="Tolerance level from 50 to 99 units per SKU"
              style={{ width: "100%" }}
              value={between50And100}
              handleChange={(e) => setBetween50And100(e.target.value)}
              inputProps={{
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  type: "number",
                },
              }}
            />
            <CustomTextField
              label="Tolerance level for over 100 units per SKU"
              style={{ width: "100%" }}
              value={above100}
              handleChange={(e) => setAbove100(e.target.value)}
              inputProps={{
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  type: "number",
                },
              }}
            />
            <Stack
              style={{
                flexDirection: "row",
                gap: 10,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CustomButton
                label="Save"
                onClick={handleSave}
                disabled={!allValid || saving || brandTolerancesLoading}
                style={{ width: 120, background: "white" }}
                loader={
                  saving ||
                  (brandTolerancesLoading && (
                    <CircularProgress
                      size={20}
                      color="inherit"
                      style={{ marginLeft: "10px" }}
                    />
                  ))
                }
              />
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};

export default withToleranceGuard(TolerancePage);
